// @ts-check
import React from 'react';
import dayjs from 'dayjs';

import FullScreenError from 'components/legacy/FullScreenError';
import Actions from 'components/HostingDetailsActions';
import Attributes from 'components/HostingDetailsAttributes';
import PageLayout, { PageLayoutInsetContent } from 'components/PageLayout';
import CancellationAlert from 'components/CancellationAlert';
import hostingDetailsAlsowith from 'pages/servers/details/hostingDetailsAlsowith';
import useAssetDetails from 'modules/queries/asset/useDetails';
import NotFound from 'pages/NotFound';
import { Redirect, useParams } from 'react-router-dom';
import { getErrorStringHelper } from 'utility/redux/selectorHelperFunctions/apiModules';
import useDocumentTitle from 'utility/hooks/useDocumentTitle';
import useTriggerServerViewEvent from 'utility/hooks/useTriggerServerViewEvent';

/** @typedef {import('@liquidweb-dev/undercurrent').Theme['palette']} Palette */

/**
 * A layout for the BeyondHosting server details pages.
 * @param {object} props
 * @param {React.ReactNode} props.children
 */

interface BeyondHostingLayoutProps {
	children: React.ReactNode;
}

const BeyondHostingLayout = ({ children }: BeyondHostingLayoutProps) => {
	/** @type {{uniqId: string}} */

	const { uniqId } = useParams<{ uniqId: string }>();
	const {
		data: assetDetailsData,
		isLoading: assetDetailsIsLoading,
		isError: assetDetailsIsError,
		error: assetDetailsError,
	} = useAssetDetails(
		{
			uniq_id: uniqId,
			alsowith: hostingDetailsAlsowith,
		},
		{
			refetchInterval: 30000,
		},
	);

	useDocumentTitle(assetDetailsData?.domain || 'Beyond Hosting');
	useTriggerServerViewEvent({ uniqId });

	if (assetDetailsIsError && assetDetailsError.statusCode === 404) {
		return <NotFound />;
	}

	if (assetDetailsIsError) {
		return (
			<FullScreenError content={getErrorStringHelper(assetDetailsIsError)} />
		);
	}

	const { domain, isBeyondHosting } = assetDetailsData || {};

	// redirect non BeyondHosting servers
	if (isBeyondHosting === false) {
		return <Redirect to={`/servers/${uniqId}`} />;
	}

	const cancellationDate =
		assetDetailsData?.scheduledCancellationDate?.DateTime;
	const isCancellationScheduled = cancellationDate
		? dayjs(cancellationDate).isSameOrAfter(dayjs(), 'day')
		: false;

	const nextRenewalDate = dayjs(
		assetDetailsData?.nextInvoiceDate?.DateTime,
	).format('MMMM D, YYYY');

	return (
		<>
			{isCancellationScheduled && (
				<CancellationAlert
					cancellationDate={cancellationDate}
					uniqId={uniqId}
					title={domain}
					nextRenewalDate={nextRenewalDate}
				/>
			)}
			<PageLayout
				title={domain || ''}
				attributesSlot={<Attributes />}
				actionsSlot={<Actions />}
				isLoading={assetDetailsIsLoading}
				variant="dynamic"
			>
				<PageLayoutInsetContent sx={{ flexGrow: 1 }}>
					{children}
				</PageLayoutInsetContent>
			</PageLayout>
		</>
	);
};

export default BeyondHostingLayout;
